const NARA_AUTH_STORAGE = 'nara.auth.session';

function naraGiftConfig(root) {
  const url = (root?.dataset.supabaseUrl || '').replace(/\/$/, '');
  const key = root?.dataset.supabaseAnonKey || '';
  return { url, key, ready: Boolean(url && key) };
}

function naraGiftSession() {
  try {
    const raw = window.localStorage.getItem(NARA_AUTH_STORAGE);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

async function naraGiftRequest(config, path, { method = 'GET', body, token } = {}) {
  const headers = {
    apikey: config.key,
    Authorization: `Bearer ${token || config.key}`,
    'Content-Type': 'application/json',
  };
  const response = await fetch(`${config.url}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  return { ok: response.ok, status: response.status, payload };
}

class NaraGiftFlow extends HTMLElement {
  connectedCallback() {
    this.config = naraGiftConfig(this);
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.step = 1;
    this.product = null;
    this.querySelectorAll('[data-nara-gift-pick]').forEach((button) => {
      button.addEventListener('click', () => this.pick(button), { signal });
    });
    this.querySelector('[data-nara-gift-back]')?.addEventListener('click', () => this.showStep(1), { signal });
    this.querySelector('[data-nara-gift-form]')?.addEventListener('submit', (event) => this.checkout(event), { signal });
    this.loadCatalog();
    this.maybeConfirm();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  async loadCatalog() {
    if (!this.config.ready) {
      this.status('Gift checkout is not connected on this website yet.', 'error');
      return;
    }
    const { ok, payload } = await naraGiftRequest(this.config, '/functions/v1/nara-gift?action=catalog');
    if (!ok || !payload?.products) {
      this.status('Gift options could not be loaded. Try again.', 'error');
      return;
    }
    this.stripeReady = Boolean(payload.stripe_ready);
    payload.products.forEach((product) => {
      const button = this.querySelector(`[data-nara-gift-pick="${product.code}"]`);
      if (!button) return;
      const price = button.querySelector('[data-nara-gift-price]');
      if (price) price.textContent = product.amount_label;
      button.dataset.product = JSON.stringify(product);
    });
    if (!this.stripeReady) {
      this.status('Stripe is still in setup. Gift checkout is connected but not taking live payments until keys are set.', 'info');
    }
  }

  pick(button) {
    let product = null;
    try {
      product = JSON.parse(button.dataset.product || 'null');
    } catch {
      product = null;
    }
    if (!product) return;
    this.product = product;
    this.querySelectorAll('[data-nara-gift-pick]').forEach((el) => {
      el.setAttribute('aria-pressed', el === button ? 'true' : 'false');
    });
    const summary = this.querySelector('[data-nara-gift-summary]');
    if (summary) {
      summary.textContent = `${product.plan_label} · ${product.duration_label} · ${product.amount_label}`;
    }
    const cardPlan = this.querySelector('[data-nara-gift-card-plan]');
    if (cardPlan) cardPlan.textContent = `${product.plan_label} · ${product.duration_label}`;
    this.showStep(2);
  }

  showStep(step) {
    this.step = step;
    this.querySelectorAll('[data-nara-gift-step]').forEach((panel) => {
      panel.hidden = Number(panel.dataset.naraGiftStep) !== step;
    });
  }

  status(message, tone) {
    const el = this.querySelector('[data-nara-gift-status]');
    if (!el) return;
    el.hidden = !message;
    el.textContent = message || '';
    el.dataset.tone = tone || 'info';
  }

  async checkout(event) {
    event.preventDefault();
    if (!this.product) {
      this.status('Choose a membership first.', 'error');
      this.showStep(1);
      return;
    }
    const form = event.target;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    const submit = form.querySelector('[data-nara-gift-submit]');
    if (submit) {
      submit.disabled = true;
      submit.dataset.label = submit.textContent;
      submit.textContent = 'Opening secure checkout…';
    }
    this.status('', 'info');
    try {
      const { ok, payload } = await naraGiftRequest(this.config, '/functions/v1/nara-gift', {
        method: 'POST',
        body: {
          action: 'checkout',
          product_code: this.product.code,
          recipient_name: form.elements.recipient_name.value,
          recipient_email: form.elements.recipient_email.value,
          buyer_name: form.elements.buyer_name.value,
          buyer_email: form.elements.buyer_email.value,
          message: form.elements.message.value,
          send_mode: 'now',
        },
      });
      if (!ok || !payload?.url) {
        this.status(
          payload?.error === 'checkout_unconfigured'
            ? 'Stripe keys are not set yet. Gift checkout cannot start.'
            : 'Checkout could not start. Check the details and try again.',
          'error',
        );
        return;
      }
      window.location.assign(payload.url);
    } catch {
      this.status('Checkout could not start. Try again.', 'error');
    } finally {
      if (submit) {
        submit.disabled = false;
        submit.textContent = submit.dataset.label || 'Continue to secure checkout';
      }
    }
  }

  async maybeConfirm() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('status') === 'cancel') {
      this.status('Checkout was canceled. Your gift was not purchased.', 'info');
      return;
    }
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    this.showStep(4);
    const { ok, payload } = await naraGiftRequest(
      this.config,
      `/functions/v1/nara-gift?action=confirm&session_id=${encodeURIComponent(sessionId)}`,
    );
    const box = this.querySelector('[data-nara-gift-confirm]');
    if (!ok || !payload?.ok) {
      if (box) box.innerHTML = '<p>Payment is still confirming. Refresh this page in a moment.</p>';
      return;
    }
    if (!box) return;
    box.innerHTML = `
      <p class="nara-gift__confirm-kicker">Payment confirmed</p>
      <h2>Your Nara gift is on its way.</h2>
      <ul>
        <li>${payload.plan_label} · ${payload.duration_label}</li>
        <li>For ${payload.recipient_name}</li>
        <li>${payload.amount_label}</li>
        <li>Order ${payload.public_id}</li>
      </ul>
      <p>A receipt is emailed to you. The recipient receives the gift card by email.</p>
    `;
  }
}

class NaraRedeemFlow extends HTMLElement {
  connectedCallback() {
    this.config = naraGiftConfig(this);
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.token = new URLSearchParams(window.location.search).get('t') || '';
    this.querySelector('[data-nara-redeem-login]')?.addEventListener('submit', (event) => this.login(event), { signal });
    this.querySelector('[data-nara-redeem-signup]')?.addEventListener('submit', (event) => this.signup(event), { signal });
    this.querySelector('[data-nara-redeem-activate]')?.addEventListener('click', () => this.activate(), { signal });
    this.boot();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  setPanel(name) {
    this.querySelectorAll('[data-nara-redeem-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.naraRedeemPanel !== name;
    });
  }

  status(message, tone) {
    this.querySelectorAll('[data-nara-redeem-status]').forEach((el) => {
      if (el.closest('[hidden]')) return;
      el.hidden = !message;
      el.textContent = message || '';
      el.dataset.tone = tone || 'info';
    });
  }

  async boot() {
    if (!this.token) {
      this.setPanel('invalid');
      return;
    }
    const { payload } = await naraGiftRequest(
      this.config,
      `/functions/v1/nara-gift?action=lookup&t=${encodeURIComponent(this.token)}`,
    );
    if (!payload?.ok) {
      this.setPanel(payload?.error === 'already_used' ? 'used' : 'invalid');
      const plan = this.querySelector('[data-nara-redeem-used-plan]');
      if (plan && payload?.plan_label) plan.textContent = payload.plan_label;
      return;
    }
    this.querySelectorAll('[data-nara-redeem-plan]').forEach((el) => {
      el.textContent = `${payload.plan_label} · ${payload.duration_label}`;
    });
    const session = naraGiftSession();
    if (session?.access_token) this.setPanel('confirm');
    else this.setPanel('auth');
  }

  async login(event) {
    event.preventDefault();
    const form = event.target;
    const { ok, payload } = await naraGiftRequest(this.config, '/auth/v1/token?grant_type=password', {
      method: 'POST',
      body: {
        email: form.elements.email.value.trim(),
        password: form.elements.password.value,
      },
    });
    if (!ok || !payload?.access_token) {
      this.status('We couldn’t sign you in. Check your details and try again.', 'error');
      return;
    }
    window.localStorage.setItem(NARA_AUTH_STORAGE, JSON.stringify(payload));
    this.setPanel('confirm');
  }

  async signup(event) {
    event.preventDefault();
    const form = event.target;
    if (form.elements.password.value !== form.elements.confirm.value) {
      this.status('The passwords do not match.', 'error');
      return;
    }
    const { ok, payload } = await naraGiftRequest(this.config, '/auth/v1/signup', {
      method: 'POST',
      body: {
        email: form.elements.email.value.trim(),
        password: form.elements.password.value,
      },
    });
    if (!ok || payload?.error || payload?.msg) {
      this.status(payload?.msg || 'We couldn’t create this account. Try again.', 'error');
      return;
    }
    if (payload?.access_token) {
      window.localStorage.setItem(NARA_AUTH_STORAGE, JSON.stringify(payload));
      this.setPanel('confirm');
      return;
    }
    this.status('Check your email to confirm this account, then return to activate your gift.', 'success');
  }

  async activate() {
    const session = naraGiftSession();
    if (!session?.access_token) {
      this.setPanel('auth');
      return;
    }
    const button = this.querySelector('[data-nara-redeem-activate]');
    if (button) {
      button.disabled = true;
      button.textContent = 'Activating…';
    }
    const { ok, payload } = await naraGiftRequest(this.config, '/functions/v1/nara-gift', {
      method: 'POST',
      token: session.access_token,
      body: { action: 'redeem', token: this.token },
    });
    if (!ok || !payload?.ok) {
      this.status(
        payload?.error === 'already_used'
          ? 'This gift has already been activated.'
          : payload?.error === 'rate_limited'
            ? 'Too many attempts. Try again later.'
            : 'This gift could not be activated.',
        'error',
      );
      if (button) {
        button.disabled = false;
        button.textContent = 'Activate membership';
      }
      return;
    }
    const done = this.querySelector('[data-nara-redeem-done-copy]');
    if (done && payload.result === 'queued') {
      done.textContent =
        'Your App Store or Google Play membership stays as it is. This gift starts when that period ends.';
    }
    this.setPanel('done');
  }
}

class NaraGiftAdmin extends HTMLElement {
  connectedCallback() {
    this.config = naraGiftConfig(this);
    this.querySelector('[data-nara-gift-admin-refresh]')?.addEventListener('click', () => this.load());
    this.load();
  }

  async load() {
    const session = naraGiftSession();
    const box = this.querySelector('[data-nara-gift-admin-body]');
    if (!session?.access_token) {
      if (box) box.innerHTML = '<p>Log in with an admin Nara account first.</p>';
      return;
    }
    const { ok, payload } = await naraGiftRequest(this.config, '/functions/v1/nara-gift', {
      method: 'POST',
      token: session.access_token,
      body: { action: 'admin' },
    });
    if (!ok || !payload?.ok) {
      if (box) box.innerHTML = '<p>This account cannot open Gift Memberships.</p>';
      return;
    }
    const t = payload.totals;
    const rows = (payload.orders || [])
      .map(
        (row) => `
        <tr>
          <td>${row.public_id}</td>
          <td>${row.plan_label} · ${row.duration_months} mo</td>
          <td>${row.payment_status}</td>
          <td>${row.delivery_status}</td>
          <td>${row.activation_status}</td>
          <td>${row.recipient_email}</td>
          <td><button type="button" data-nara-gift-resend="${row.public_id}">Resend</button></td>
        </tr>`
      )
      .join('');
    if (box) {
      box.innerHTML = `
        <dl class="nara-gift-admin__totals">
          <div><dt>Sold</dt><dd>${t.sold}</dd></div>
          <div><dt>Revenue</dt><dd>${t.revenue_label}</dd></div>
          <div><dt>Sent</dt><dd>${t.sent}</dd></div>
          <div><dt>Pending</dt><dd>${t.pending}</dd></div>
          <div><dt>Activated</dt><dd>${t.activated}</dd></div>
          <div><dt>Unused</dt><dd>${t.unused}</dd></div>
          <div><dt>Refunds</dt><dd>${t.refunded}</dd></div>
        </dl>
        <div class="nara-gift-admin__table-wrap">
          <table>
            <thead><tr><th>Order</th><th>Gift</th><th>Pay</th><th>Delivery</th><th>Activation</th><th>Recipient</th><th></th></tr></thead>
            <tbody>${rows || '<tr><td colspan="7">No gifts yet.</td></tr>'}</tbody>
          </table>
        </div>
      `;
      box.querySelectorAll('[data-nara-gift-resend]').forEach((button) => {
        button.addEventListener('click', () => this.resend(button.dataset.naraGiftResend, session.access_token));
      });
    }
  }

  async resend(publicId, token) {
    await naraGiftRequest(this.config, '/functions/v1/nara-gift', {
      method: 'POST',
      token,
      body: { action: 'admin-resend', public_id: publicId },
    });
    this.load();
  }
}

if (!customElements.get('nara-gift-flow')) customElements.define('nara-gift-flow', NaraGiftFlow);
if (!customElements.get('nara-redeem-flow')) customElements.define('nara-redeem-flow', NaraRedeemFlow);
if (!customElements.get('nara-gift-admin')) customElements.define('nara-gift-admin', NaraGiftAdmin);
